// Third-party
import get from 'lodash/get';
import moment from 'moment';
import ordinal from 'ordinal';

// Utils
import { userHasPermissionEnabled, PERMISSIONS, userHasPermission } from '../../../../utils/permissions';
import { getComparisonItemTotalEquity, splitCreditQualityForDisplay, comparisonIsReadOnly } from '../../../../utils/comparison';
import {
  BOND_TAG,
  CAPITALIZATION_CATEGORIES,
  COMPARISON_CATEGORIES,
  COMPARISON_ITEM_TYPES,
  DISPLAYED_CREDIT_QUALITY_CATEGORIES,
  EQUITY_STYLE_CATEGORIES,
  FIXED_INCOME_CREDIT_RATING_CATEGORIES,
  FIXED_INCOME_DURATION_CATEGORIES,
  FIXED_INCOME_SECTORS,
  FIXED_INCOME_SECTORS_US,
  FUND_SOURCE,
  MATURITY_CATEGORIES,
  MATURITY_CATEGORIES_FACTSET,
  PERIOD_LIST,
  REGIONS,
  SINCE_INCEPTION_ITEM_TYPES,
} from '../../../../utils/constants';
import { CURRENCY } from '../../../../utils/currency';
import { getFieldValueDisclaimer } from '../../../../utils/disclaimers/comparison';
import { DISCLAIMER_KEYS } from '../../../../utils/disclaimers/constants';
import { formatLocalizedString, formatMessage, LOCALE, formatMessageWithLocale } from '../../../../utils/i18n';
import { enumerateArray, uniqueArray } from '../../../../utils/objects';
import { getCouponDates } from '../../../../modules/proposal/utils/bonds-utils';
import { getLastNYears, periodToYear } from '../../../../utils/time';
import { validateAppMode, validateAppRegion } from '@/utils/enterprise';

// Components
import {
  BenchmarkCell,
  CheckboxCell,
  CorrelationCell,
  CurrencyCell,
  DateCell,
  GrowthCell,
  HoldingCell,
  LipperScoreCell,
  ObjectiveCell,
  PercentageCell,
  RiskRatingCell,
  StatCell,
  TextCell,
  UpDownCell,
} from '../../../components/cells';
import ESGRatingCell from '../../../components/cells/ESG/RatingCell';
import ComparisonCorrelationLabel from '../misc/ComparisonCorrelationLabel';
import ESGPercentileCell from '../../../components/cells/ESG/PercentileCell';
import ESGClassificationCell from '../../../components/cells/ESG/ClassificationCell';
import ESGCarbonIntensityCell from '../../../components/cells/ESG/CarbonIntensityCell';
import ESGTemperatureCell from '../../../components/cells/ESG/TemperatureCell';
import { InfoTooltip } from '@/views/components/tooltips';

// Constants and types
import { ESGRatingDistributionValue } from '../../../../enums/Esg';
import User from '@/types/user';
import Comparison from '@/types/comparison';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonItem from '@/types/comparison-item';
import Nullable from '@/types/nullable';
import ComparisonField from '@/types/comparison-field';
import AppRegion from '@/types/app-region';
import RiskPeriods from '@/types/risk-periods';
import Locale from '@/types/locale';
import { DETAILED_ASSET_ALLOCATION } from '@/enums/AssetAllocation';
import AppMode from '@/types/app-mode';
import { DATA_PROVIDER, getDataProviderForDisplay, isPreferredProviderRefinitiv, getPreferredDataProvider, getDataProviderName } from '@/utils/users';

import React from 'react';

const TIME_BASED_DISCLAIMERS = {
  [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_CLOSED, DISCLAIMER_KEYS.FUND_NO_DATA, DISCLAIMER_KEYS.FUND_NO_DATA_BEFORE_DATE],
  [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_CLOSED, DISCLAIMER_KEYS.STOCK_NO_DATA, DISCLAIMER_KEYS.STOCK_NO_DATA_BEFORE_DATE],
  [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
    DISCLAIMER_KEYS.PORTFOLIO_CLOSED_HOLDINGS,
    DISCLAIMER_KEYS.PORTFOLIO_NO_DATA,
    DISCLAIMER_KEYS.PORTFOLIO_NO_DATA_BEFORE_DATE_SHORT,
    DISCLAIMER_KEYS.PORTFOLIO_NO_DATA_BEFORE_DATE,
  ],
  [COMPARISON_ITEM_TYPES.BENCHMARK]: [DISCLAIMER_KEYS.BENCHMARK_CLOSED, DISCLAIMER_KEYS.BENCHMARK_NO_DATA, DISCLAIMER_KEYS.BENCHMARK_NO_DATA_BEFORE_DATE],
};

const getAsAtDate = (item: ComparisonItem, singleDatePath: string, portfolioDatesPath?: string) => {
  const formatDates = (dates: Date[]) => {
    const formattedDates = dates.filter(d => d).map(d => moment.utc(d).format(formatMessage('Common.DateFormats.Short')));
    const uniqueFormattedDates = uniqueArray(formattedDates);
    return enumerateArray(uniqueFormattedDates, formatMessage('Common.And'));
  };

  const singleDate = get(item, singleDatePath) as Nullable<Date>;
  if (singleDate) {
    return formatDates([singleDate]);
  }

  if (portfolioDatesPath) {
    return formatDates(Object.values(get(item, portfolioDatesPath, {})));
  }

  return null;
};

const getShortHistoryValueDisclaimer = (key: string, comparison: Comparison) => (item: ComparisonItem) => getFieldValueDisclaimer(comparison, item, key);

const getRiskMetricsShortHistoryValueDisclaimer = (comparison: Comparison) => {
  // Use performance disclaimers for same period - data is the same.
  const key = `performance_percentGrowth_${comparison.riskPeriod}`;

  return getShortHistoryValueDisclaimer(key, comparison);
};

const getCalendarYearFields = (comparison: Comparison, forAccessibleTable = false) => {
  // For accessible table, always show 10 years for better screen reader experience
  // For main table, use the comparison's calendar year period setting
  const periodToUse = forAccessibleTable ? '10Y' : comparison.calendarYearPeriod;
  const years = getLastNYears(periodToYear(periodToUse));

  return years.map(year => {
    const key = `performance_calendarYear_${year}`;

    return {
      key,
      label: year.toString(),
      cell: GrowthCell,
      disclaimers: TIME_BASED_DISCLAIMERS,
      getValueDisclaimer: getShortHistoryValueDisclaimer(key, comparison),
    };
  });
};

const getPerformanceFields = (comparison: Comparison, user?: User, locale?: Locale) => {
  const localeDateFormat = formatMessageWithLocale('Common.DateFormats.Short', locale || LOCALE.EN_CA);
  const { commonInception } = comparison;
  const commonInceptionDate = `${commonInception?.startTimestamp ? moment.utc(commonInception.startTimestamp).format(localeDateFormat) : ''}`;

  const fields: ComparisonField[] = PERIOD_LIST.map(period => {
    const key = `performance_percentGrowth_${period}`;

    return {
      key,
      label: formatMessage(`Comparison.Table.Performance.${period}`),
      cell: GrowthCell,
      disclaimers: TIME_BASED_DISCLAIMERS,
      getValueDisclaimer: getShortHistoryValueDisclaimer(key, comparison),
    };
  });

  fields.push({
    key: 'performance_percentGrowth_INC',
    label: formatMessage('Comparison.Table.Performance.INC'),
    info: formatMessage('Comparison.Table.SinceInceptionTooltip'),
    cell: GrowthCell,
    itemTypes: userHasPermission(user, PERMISSIONS.COMMON_INCEPTION) ? SINCE_INCEPTION_ITEM_TYPES : [COMPARISON_ITEM_TYPES.FUND],
    disclaimers: TIME_BASED_DISCLAIMERS,
    getValueDisclaimer: getShortHistoryValueDisclaimer('performance_percentGrowth_INC', comparison),
  });

  if (userHasPermission(user, PERMISSIONS.COMMON_INCEPTION)) {
    fields.push({
      key: 'performance_percentGrowth_INCDate',
      label: formatMessage('Comparison.Table.KeyMetrics.InceptionDate'),
      cell: DateCell,
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO],
      accessor: item => item.dates.inception,
    });

    fields.push({
      key: 'performance_percentGrowth_CI',
      label: formatMessage('Comparison.Table.Performance.CI'),
      info: formatMessage('Comparison.Table.CommonInceptionTooltip', { date: commonInceptionDate }),
      cell: GrowthCell,
      hidden: comparison.items.length <= 1,
      itemTypes: [...SINCE_INCEPTION_ITEM_TYPES, COMPARISON_ITEM_TYPES.BENCHMARK],
    });

    fields.push({
      key: 'performance_percentGrowth_CIDate',
      label: formatMessage('Comparison.Table.Performance.CommonInceptionDate'),
      cell: DateCell,
      hidden: comparison.items.length <= 1,
      itemTypes: [...SINCE_INCEPTION_ITEM_TYPES, COMPARISON_ITEM_TYPES.BENCHMARK],
      accessor: () => commonInception?.startTimestamp,
    });
  }

  return fields;
};

const getTopHoldingsField = (count: number = 10, showNumerical?: boolean) => {
  const fields: ComparisonField[] = [];

  for (let i = 0; i < count; i++) {
    fields.push({
      key: `detailedHoldings_list[${i}]`,
      label: formatMessage(`Common.${showNumerical ? `Numerical.${i + 1}` : `Ordinal.${ordinal(i + 1)}`}`),
      cell: HoldingCell,
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MISSING_TOP_HOLDINGS],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_TOP_HOLDINGS],
      },
    });
  }

  return fields;
};

const getCountryAllocationsField = (count: number = 5) => {
  const fields: ComparisonField[] = [];

  for (let i = 0; i < count; i++) {
    fields.push({
      key: `allocations_country[${i}]`,
      label: formatMessage(`Common.Ordinal.${ordinal(i + 1)}`),
      cell: HoldingCell,
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MISSING_COUNTRY_ALLOC],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_COUNTRY_ALLOC],
      },
    });
  }

  return fields;
};

const getCreditQualityFields = () =>
  DISPLAYED_CREDIT_QUALITY_CATEGORIES.map(category => ({
    key: `allocations_creditQuality["${category}"]`,
    label: formatMessage(`Comparison.Table.CreditQuality.${category}`),
    cell: PercentageCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_CREDIT_QUALITY_NOT_APPLICABLE],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_CREDIT_QUALITY_NOT_APPLICABLE],
    },
    accessor: (item: ComparisonItem) => {
      if (!item.allocations || !item.allocations.creditQuality || !item.allocations.creditQuality.length) {
        return undefined;
      }

      const creditQuality = splitCreditQualityForDisplay(item);

      return (creditQuality && creditQuality[category]) || 0;
    },
  }));

const getEquityStyleBreakdownInfo = (user: User): string => {
  const preferredProvider = getPreferredDataProvider(user, 'fundPortfolio.investmentInfo.capitalization');

  if (isPreferredProviderRefinitiv(preferredProvider)) {
    return formatMessage('Comparison.Table.EquityStyle.TooltipInfo');
  }

  return formatMessage('Comparison.Table.SectionProvidedByProvider', {
    preferredDataProvider: getDataProviderName(user, 'fundPortfolio.investmentInfo.capitalization'),
  });
};

const getEquityStyleBreakdownFields = (hidden = false): ComparisonField[] => [
  {
    key: 'equityStyle_value',
    label: formatMessage('Comparison.Table.EquityStyle.Value'),
    cell: PercentageCell,
    itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    accessor: (item: ComparisonItem) => {
      if (!item.investmentInfo || !item.investmentInfo.style) {
        return null;
      }

      const value = item.investmentInfo.style.find(s => s.name[LOCALE.EN_CA] === EQUITY_STYLE_CATEGORIES.VALUE);
      return value != null ? value.weight : null;
    },
    hidden,
  },
  {
    key: 'equityStyle_blend',
    label: formatMessage('Comparison.Table.EquityStyle.Blend'),
    cell: PercentageCell,
    itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    accessor: (item: ComparisonItem) => {
      if (!item.investmentInfo || !item.investmentInfo.style) {
        return null;
      }

      const value = item.investmentInfo.style.find(s => s.name[LOCALE.EN_CA] === EQUITY_STYLE_CATEGORIES.BLEND);
      return value != null ? value.weight : null;
    },
    hidden,
  },
  {
    key: 'equityStyle_growth',
    label: formatMessage('Comparison.Table.EquityStyle.Growth'),
    cell: PercentageCell,
    itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    accessor: (item: ComparisonItem) => {
      if (!item.investmentInfo || !item.investmentInfo.style) {
        return null;
      }

      const value = item.investmentInfo.style.find(s => s.name[LOCALE.EN_CA] === EQUITY_STYLE_CATEGORIES.GROWTH);
      return value != null ? value.weight : null;
    },
    hidden,
  },
  {
    key: 'equityStyle_largeCap',
    label: formatMessage('Comparison.Table.EquityStyle.LargeCap'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      if (!item.investmentInfo || !item.investmentInfo.capitalization) {
        return null;
      }

      const value = item.investmentInfo.capitalization.find(s => s.name[LOCALE.EN_CA] === CAPITALIZATION_CATEGORIES.LARGE);
      return value != null ? value.weight : null;
    },
  },
  {
    key: 'equityStyle_midCap',
    label: formatMessage('Comparison.Table.EquityStyle.MidCap'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      if (!item.investmentInfo || !item.investmentInfo.capitalization) {
        return null;
      }

      const value = item.investmentInfo.capitalization.find(s => s.name[LOCALE.EN_CA] === CAPITALIZATION_CATEGORIES.MEDIUM);
      return value != null ? value.weight : null;
    },
  },
  {
    key: 'equityStyle_smallCap',
    label: formatMessage('Comparison.Table.EquityStyle.SmallCap'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      if (!item.investmentInfo || !item.investmentInfo.capitalization) {
        return null;
      }

      const value = item.investmentInfo.capitalization.find(s => s.name[LOCALE.EN_CA] === CAPITALIZATION_CATEGORIES.SMALL);
      return value != null ? value.weight : null;
    },
  },
];

const getFixedIncomeStyleBreakdownFields = (): ComparisonField[] => {
  const disclaimers = {
    [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_FIXED_INCOME_STYLE_NOT_APPLICABLE],
    [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_FIXED_INCOME_STYLE_NOT_APPLICABLE],
  };

  return [
    {
      key: 'fixedIncomeStyle_longTerm',
      label: formatMessage('Comparison.Table.FixedIncomeStyle.LongTerm'),
      cell: PercentageCell,
      disclaimers,
      accessor: (item: ComparisonItem) => {
        if (!item.investmentInfo || !item.investmentInfo.durationClass || !item.investmentInfo.durationClass.length) {
          return null;
        }

        const value = item.investmentInfo.durationClass.find(s => s.name[LOCALE.EN_CA] === FIXED_INCOME_DURATION_CATEGORIES.LONG);
        return value != null ? value.weight : null;
      },
    },
    {
      key: 'fixedIncomeStyle_mediumTerm',
      label: formatMessage('Comparison.Table.FixedIncomeStyle.MediumTerm'),
      cell: PercentageCell,
      disclaimers,
      accessor: (item: ComparisonItem) => {
        if (!item.investmentInfo || !item.investmentInfo.durationClass || !item.investmentInfo.durationClass.length) {
          return null;
        }

        const value = item.investmentInfo.durationClass.find(s => s.name[LOCALE.EN_CA] === FIXED_INCOME_DURATION_CATEGORIES.MEDIUM);
        return value != null ? value.weight : null;
      },
    },
    {
      key: 'fixedIncomeStyle_shortTerm',
      label: formatMessage('Comparison.Table.FixedIncomeStyle.ShortTerm'),
      cell: PercentageCell,
      disclaimers,
      accessor: (item: ComparisonItem) => {
        if (!item.investmentInfo || !item.investmentInfo.durationClass || !item.investmentInfo.durationClass.length) {
          return null;
        }

        const value = item.investmentInfo.durationClass.find(s => s.name[LOCALE.EN_CA] === FIXED_INCOME_DURATION_CATEGORIES.SHORT);
        return value != null ? value.weight : null;
      },
    },
    {
      key: 'fixedIncomeStyle_highCredit',
      label: formatMessage('Comparison.Table.FixedIncomeStyle.HighCredit'),
      cell: PercentageCell,
      disclaimers,
      accessor: (item: ComparisonItem) => {
        if (!item.investmentInfo || !item.investmentInfo.creditRatingClass || !item.investmentInfo.creditRatingClass.length) {
          return null;
        }

        const value = item.investmentInfo.creditRatingClass.find(s => s.name[LOCALE.EN_CA] === FIXED_INCOME_CREDIT_RATING_CATEGORIES.HIGH);
        return value != null ? value.weight : null;
      },
    },
    {
      key: 'fixedIncomeStyle_mediumCredit',
      label: formatMessage('Comparison.Table.FixedIncomeStyle.MediumCredit'),
      cell: PercentageCell,
      disclaimers,
      accessor: (item: ComparisonItem) => {
        if (!item.investmentInfo || !item.investmentInfo.creditRatingClass || !item.investmentInfo.creditRatingClass.length) {
          return null;
        }

        const value = item.investmentInfo.creditRatingClass.find(s => s.name[LOCALE.EN_CA] === FIXED_INCOME_CREDIT_RATING_CATEGORIES.MEDIUM);
        return value != null ? value.weight : null;
      },
    },
    {
      key: 'fixedIncomeStyle_lowCredit',
      label: formatMessage('Comparison.Table.FixedIncomeStyle.LowCredit'),
      cell: PercentageCell,
      disclaimers,
      accessor: (item: ComparisonItem) => {
        if (!item.investmentInfo || !item.investmentInfo.creditRatingClass || !item.investmentInfo.creditRatingClass.length) {
          return null;
        }

        const value = item.investmentInfo.creditRatingClass.find(s => s.name[LOCALE.EN_CA] === FIXED_INCOME_CREDIT_RATING_CATEGORIES.LOW);
        return value != null ? value.weight : null;
      },
    },
  ];
};

const getEquityMetricsFields = (user: User): ComparisonField[] => {
  const isAppModeFactset = validateAppMode(user.appConfig.mode, AppMode.FACT_SET);
  return [
    {
      key: 'equity_allocation',
      label: formatMessage('Comparison.Table.EquityMetrics.EquityAllocation'),
      accessor: item => getComparisonItemTotalEquity(item),
      cell: PercentageCell,
    },
    {
      key: 'investmentInfo_peRatio',
      label: formatMessage('Comparison.Table.EquityMetrics.PERatio'),
      info: formatMessage('Comparison.Table.FieldProvidedByProvider', {
        preferredDataProvider: getDataProviderName(user, 'fundPortfolio.investmentInfo.peRatio'),
      }),
      cell: StatCell,
    },
    {
      key: 'investmentInfo_pbRatio',
      label: formatMessage('Comparison.Table.EquityMetrics.PBRatio'),
      info: formatMessage('Comparison.Table.FieldProvidedByProvider', {
        preferredDataProvider: getDataProviderName(user, 'fundPortfolio.investmentInfo.pbRatio'),
      }),
      cell: StatCell,
    },
    {
      key: 'investmentInfo_distributionYield',
      label: formatMessage('Comparison.Table.EquityMetrics.DistributionYield'),
      cell: PercentageCell,
    },
    {
      key: 'characteristics_returnOnEquity',
      label: formatMessage('Comparison.Table.EquityMetrics.ReturnOnEquity'),
      info: isAppModeFactset
        ? formatMessage('Comparison.Table.EquityMetrics.ReturnProvidedFactset')
        : formatMessage('Comparison.Table.EquityMetrics.ReturnProvidedFundata'),
      cell: PercentageCell,
    },
  ];
};

const getFixedIncomeMetricsFields = (user: User, comparison: Comparison): ComparisonField[] => {
  const disclaimers = {
    [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_FIXED_INCOME_METRICS_NOT_APPLICABLE],
    [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_FIXED_INCOME_METRICS_NOT_APPLICABLE],
  };

  const getYieldToMaturityDisclaimer = (item: ComparisonItem) => {
    // If the comparison item is collapsible
    if (item.type === COMPARISON_ITEM_TYPES.GROUP || item.type === COMPARISON_ITEM_TYPES.PORTFOLIO) {
      const sources = item.holdings.reduce((acc, holding) => {
        const yieldToMaturity = holding.metrics?.yieldToMaturity;
        if (yieldToMaturity != null && yieldToMaturity.source != null && yieldToMaturity.source === FUND_SOURCE.FUNDATA) {
          if (holding.symbol) {
            acc.push(holding.symbol);
          }
        }
        return acc;
      }, [] as string[]);

      if (!sources.length) {
        return null;
      }

      return {
        message: formatMessage('Comparison.Table.FieldProvidedFundataForFunds', { funds: sources.join(', ') }),
        title: formatMessage('Disclaimers.YieldToMaturityFundata.Title'),
      };
    }

    if (item.characteristics?.yieldToMaturity?.source === FUND_SOURCE.FUNDATA) {
      return {
        message: formatMessage('Disclaimers.YieldToMaturityFundata.Message'),
        title: formatMessage('Disclaimers.YieldToMaturityFundata.Title'),
      };
    }
    return null;
  };

  const hasBondItems = comparison.items.some(item => item._id.startsWith(BOND_TAG));
  const portfolios = comparison.items.filter(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);
  const hasExpandedPortfolioWithBonds = portfolios.some(
    portfolio => portfolio.isExpanded && portfolio.holdings.some(holding => holding.id.startsWith(BOND_TAG)),
  );
  const comparisonHasVisibleBonds = hasBondItems || hasExpandedPortfolioWithBonds;

  return [
    {
      key: 'allocations_asset["Fixed Income"]',
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.FixedIncomeAllocation'),
      cell: PercentageCell,
      disclaimers,
    },
    {
      key: 'characteristics_averageCoupon',
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.averageCoupon'),
      cell: PercentageCell,
      disclaimers,
    },
    {
      key: 'characteristics_averageQuality',
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.averageQuality'),
      cell: TextCell,
      disclaimers,
    },
    {
      key: 'characteristics_faceValue',
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.faceValue'),
      cell: CurrencyCell,
      disclaimers,
      accessor: (item: ComparisonItem) => ({
        value: item.characteristics?.faceValue,
        currency: item.currency,
      }),
      hidden: !comparisonHasVisibleBonds,
    },
    {
      key: 'characteristics_couponDates',
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.couponDates'),
      cell: TextCell,
      disclaimers,
      accessor: (item: ComparisonItem) => getCouponDates(item.characteristics?.frequency, item.characteristics?.frequencyUnit),
      hidden: !comparisonHasVisibleBonds,
    },
    {
      key: 'characteristics_maturityDate',
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.maturityDates'),
      cell: DateCell,
      disclaimers,
      hidden: !comparisonHasVisibleBonds,
    },
    {
      key: 'characteristics_yieldToMaturity',
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.yieldToMaturity'),
      cell: PercentageCell,
      disclaimers,
      getValueDisclaimer(item: ComparisonItem) {
        return getYieldToMaturityDisclaimer(item);
      },
      accessor: (item: ComparisonItem) => item.characteristics?.yieldToMaturity?.value,
    },
    {
      key: 'characteristics_effectiveMaturity',
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.effectiveMaturity'),
      cell: StatCell,
      disclaimers,
    },
    {
      key: 'investmentInfo_modifiedDuration',
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.modifiedDuration'),
      info: formatMessage('Comparison.Table.FieldProvidedByProvider', {
        preferredDataProvider: getDataProviderName(user, 'fundPortfolio.investmentInfo.modifiedDuration'),
      }),
      cell: StatCell,
      disclaimers,
    },
    {
      key: 'investmentInfo_effectiveDuration',
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.effectiveDuration'),
      cell: StatCell,
      disclaimers,
    },
  ];
};

const getESGOverviewFields = (comparison: Comparison): ComparisonField[] => [
  {
    key: 'esg_rating_value', // should be value
    label: formatMessage('Comparison.Table.ESGOverview.ESGRating'),
    cell: ESGRatingCell,
    info: formatMessage('ESGComparisonTool.Rating.InfoText'),
  },
  {
    key: 'esg_rating_classification', // should be classification
    label: formatMessage('Comparison.Table.ESGOverview.Classification'),
    cell: ESGClassificationCell,
  },
  {
    key: 'esg_peerRank',
    label: formatMessage('Comparison.Table.ESGOverview.PeerPercentile'),
    cell: ESGPercentileCell,
  },
  {
    key: 'esg_globalRank',
    label: formatMessage('Comparison.Table.ESGOverview.GlobalPercentile'),
    cell: ESGPercentileCell,
  },
  {
    key: 'esg_carbonIntensity',
    label: formatMessage('Comparison.Table.ESGOverview.CarbonIntensity'),
    cell: ESGCarbonIntensityCell,
    info: formatMessage('ESGComparisonTool.Carbon.EducationalText'),
  },
  {
    key: 'esg_impliedTemperatureRise',
    label: formatMessage('Comparison.Table.ESGOverview.ImpliedTemperatureRise'),
    cell: ESGTemperatureCell,
    info: formatMessage('ESGComparisonTool.ImpliedTemperatureRise.EducationalText'),
  },
];

const getESGRatingDistributionFields = (comparison: Comparison) => [
  {
    key: 'esg_rating_allocation_AAA',
    label: formatMessage('ESGRating.AAA'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      const alloc = item.esg?.rating?.allocations?.find(alloc => alloc.name === 'AAA');
      return alloc?.weight;
    },
  },
  {
    key: 'esg_rating_allocation_AA',
    label: formatMessage('ESGRating.AA'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      const alloc = item.esg?.rating?.allocations?.find(alloc => alloc.name === 'AA');
      return alloc?.weight;
    },
  },
  {
    key: 'esg_rating_allocation_A',
    label: formatMessage('ESGRating.A'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      const alloc = item.esg?.rating?.allocations?.find(alloc => alloc.name === 'A');
      return alloc?.weight;
    },
  },
  {
    key: 'esg_rating_allocation_BBB',
    label: formatMessage('ESGRating.BBB'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      const alloc = item.esg?.rating?.allocations?.find(alloc => alloc.name === 'BBB');
      return alloc?.weight;
    },
  },
  {
    key: 'esg_rating_allocation_BB',
    label: formatMessage('ESGRating.BB'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      const alloc = item.esg?.rating?.allocations?.find(alloc => alloc.name === 'BB');
      return alloc?.weight;
    },
  },
  {
    key: 'esg_rating_allocation_B',
    label: formatMessage('ESGRating.B'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      const alloc = item.esg?.rating?.allocations?.find(alloc => alloc.name === 'B');
      return alloc?.weight;
    },
  },
  {
    key: 'esg_rating_allocation_CCC',
    label: formatMessage('ESGRating.CCC'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      const alloc = item.esg?.rating?.allocations?.find(alloc => alloc.name === 'CCC');
      return alloc?.weight;
    },
  },
  {
    key: 'esg_rating_allocations_NoValue',
    label: formatMessage('ESGRating.NoData'),
    cell: PercentageCell,
    accessor: (item: ComparisonItem) => {
      const alloc = item.esg?.rating?.allocations?.find(alloc => alloc.name === ESGRatingDistributionValue.NO_VALUE);
      return alloc?.weight;
    },
  },
];

const getMaturityAllocationFields = (user: User): ComparisonField[] => {
  const { mode } = user.appConfig;
  const isModeFactSet = validateAppMode(mode, AppMode.FACT_SET);

  const fixedIncomeMaturityLabels = isModeFactSet ? MATURITY_CATEGORIES_FACTSET : MATURITY_CATEGORIES;

  return Object.values(fixedIncomeMaturityLabels).map(category => ({
    key: `allocations_maturity["${category}"]`,
    label: formatMessage(`MaturityAllocations.${category}`),
    info: category === MATURITY_CATEGORIES.Other ? formatMessage('Comparison.Table.MaturityAllocations.OtherInfo') : undefined,
    cell: PercentageCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MATURITY_NOT_APPLICABLE],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MATURITY_NOT_APPLICABLE],
    },
    accessor: (item: ComparisonItem) => {
      if (!item.allocations || !item.allocations.maturity || !item.allocations.maturity.length) {
        return undefined;
      }

      const maturity = item.allocations.maturity.find(m => m.name[LOCALE.EN_CA] === category);
      return maturity ? maturity.weight : 0;
    },
  }));
};

const getFixedIncomeRegionFields = (): ComparisonField[] =>
  Object.values(REGIONS).map(region => ({
    key: `allocations_fixedIncomeRegion["${region}"]`,
    label: formatMessage(`Comparison.Table.FixedIncomeRegions.${region}`),
    cell: PercentageCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_FIXED_INCOME_REGION_NOT_APPLICABLE],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_FIXED_INCOME_REGION_NOT_APPLICABLE],
    },
    accessor: (item: ComparisonItem) => {
      if (!item || !item.allocations || !item.allocations.fixedIncomeRegion || !item.allocations.fixedIncomeRegion.length) {
        return undefined;
      }

      const weightedRegion = item.allocations.fixedIncomeRegion.find(r => r.name === region);
      return weightedRegion ? weightedRegion.weight : 0;
    },
  }));

const getFixedIncomeSectorFields = (user: User): ComparisonField[] => {
  const { mode, appRegion } = user.appConfig;
  const isModeFactSetOrContact = validateAppMode(mode, AppMode.FACT_SET) || validateAppMode(mode, AppMode.CONTACT);
  const isRegionUS = validateAppRegion(appRegion, AppRegion.US);
  const locale = isRegionUS ? LOCALE.EN_US : LOCALE.EN_CA;
  const fixedIncomeSectorLabels = isModeFactSetOrContact && isRegionUS ? FIXED_INCOME_SECTORS_US : FIXED_INCOME_SECTORS;

  return Object.keys(fixedIncomeSectorLabels).map(sectorKey => ({
    key: `allocations_fixedIncomeSector_${sectorKey}`,
    label: formatMessage(`Comparison.Table.FixedIncomeSectors.${sectorKey}`),
    cell: PercentageCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_FIXED_INCOME_SECTORS_NOT_APPLICABLE],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_FIXED_INCOME_SECTORS_NOT_APPLICABLE],
    },
    accessor: (item: ComparisonItem) => {
      if (!item.allocations || !item.allocations.fixedIncomeSector || !item.allocations.fixedIncomeSector.length) {
        return undefined;
      }

      const sector = fixedIncomeSectorLabels[sectorKey];
      const weightedRegion = item.allocations.fixedIncomeSector.find(r => r.name && r.name[locale] === sector);
      return weightedRegion ? weightedRegion.weight : 0;
    },
  }));
};

const getAssetAllocationFieldsWithSubFields = (comparison: Comparison): ComparisonField[] => {
  const fieldsWithSubAllocations = new Map<string, Set<{ enName: string; frName: string }>>();

  // Collect all unique categories and their subAllocations
  comparison.items.forEach(item => {
    const { allocations } = item;
    if (!allocations?.detailedAsset) return;

    allocations.detailedAsset.forEach(asset => {
      if (!asset.subAllocations || !asset.subAllocations.length) return;

      const assetName = formatLocalizedString(asset.name);
      if (!fieldsWithSubAllocations.has(assetName)) {
        fieldsWithSubAllocations.set(assetName, new Set());
      }

      // Add each subAllocation name to the set for this category
      asset.subAllocations.forEach(subAlloc => {
        if (subAlloc.name) {
          const enName = subAlloc.name[LOCALE.EN_CA] || '';
          const frName = subAlloc.name[LOCALE.FR_CA] || enName;

          // Use string representation for Set comparison
          const subAllocKey = JSON.stringify({ enName, frName });

          let exists = false;
          fieldsWithSubAllocations.get(assetName)?.forEach(item => {
            if (JSON.stringify(item) === subAllocKey) {
              exists = true;
            }
          });

          if (!exists && enName) {
            fieldsWithSubAllocations.get(assetName)?.add({ enName, frName });
          }
        }
      });
    });
  });

  // Create fields with subFields for fields that have subAllocations
  const fields: ComparisonField[] = [];

  fieldsWithSubAllocations.forEach((subAllocSet, assetName) => {
    const subFields: ComparisonField[] = Array.from(subAllocSet).map(({ enName, frName }) => ({
      key: `allocations_detailedAsset_sub_${assetName}_${enName}`,
      label: formatLocalizedString({
        [LOCALE.EN_CA]: enName,
        [LOCALE.FR_CA]: frName,
      }),
      cell: PercentageCell,
      accessor: (item: ComparisonItem) => {
        if (!item.allocations?.detailedAsset) return 0;

        const asset = item.allocations.detailedAsset.find(a => a.name && a.name[LOCALE.EN_CA] === assetName);

        if (!asset || !asset.subAllocations) return 0;

        const subAlloc = asset.subAllocations.find(sa => sa.name && sa.name[LOCALE.EN_CA] === enName);

        return subAlloc ? (asset.weight * subAlloc.weight) / 100 : 0;
      },
    }));

    // Create the main category field with subFields
    const fieldLabel = assetName;
    fields.push({
      key: `allocations_asset["${assetName}"]`,
      label: fieldLabel,
      cell: PercentageCell,
      accessor: (item: ComparisonItem) => {
        if (!item.allocations?.asset) return 0;
        const assetWeight = item.allocations.detailedAsset?.find(da => da.name[LOCALE.EN_CA] === assetName)?.weight || 0;
        return assetWeight;
      },
      subFields,
    });
  });

  return fields;
};

const getAssetAllocationFields = (user: User, comparison: Comparison) => {
  const { appRegion } = user.appConfig;
  const dataProvider = getDataProviderForDisplay(user);

  // Verify if the comparison items have the subAllocations
  const hasSubAllocations = comparison.items.some(item => item.allocations?.detailedAsset?.some(asset => asset.subAllocations));
  const alternativeInvestmentField = userHasPermissionEnabled(user, PERMISSIONS.PREQIN_ALT)
    ? [
        {
          key: `allocations_asset[${DETAILED_ASSET_ALLOCATION.ALTERNATIVE_INVESTMENT}]`,
          label: formatMessage('Comparison.Table.AssetDetails.AlternativeInvestment'),
          cell: PercentageCell,
        },
      ]
    : [];
  const defaultFields = [
    {
      key: 'allocations_asset["Fixed Income"]',
      label: formatMessage('Comparison.Table.AssetDetails.FixedIncome'),
      cell: PercentageCell,
    },
    ...alternativeInvestmentField,
    {
      key: 'allocations_asset["Cash"]',
      label: formatMessage('Comparison.Table.AssetDetails.Cash'),
      cell: PercentageCell,
    },
    {
      key: 'allocations_asset["Other"]',
      label: formatMessage('Comparison.Table.AssetDetails.Other'),
      cell: PercentageCell,
    },
  ];

  const fieldsWithSubFields = hasSubAllocations ? getAssetAllocationFieldsWithSubFields(comparison) : [];

  if (dataProvider === DATA_PROVIDER.FACTSET || dataProvider === DATA_PROVIDER.REFINITIV_US) {
    const fields = [
      {
        key: `allocations_asset["${DETAILED_ASSET_ALLOCATION.EQ}"]`,
        label: formatMessage(`Comparison.Table.AssetDetails.${DETAILED_ASSET_ALLOCATION.EQ}`),
        cell: PercentageCell,
        accessor: (item: ComparisonItem) => {
          const { allocations } = item;
          let value = 0;

          if (!allocations?.asset) return value;

          const eqValue = allocations.asset[DETAILED_ASSET_ALLOCATION.EQ];
          if (eqValue) return eqValue;

          const intEqValue = allocations.asset[DETAILED_ASSET_ALLOCATION.FOREIGN_EQ];
          const usEqValue = allocations.asset[DETAILED_ASSET_ALLOCATION.US_EQ];

          if (intEqValue) value += intEqValue;
          if (usEqValue) value += usEqValue;

          return value;
        },
      },
      ...defaultFields,
    ];

    // Create a map of fields by label
    const fieldMap: Map<string, ComparisonField> = new Map(fields.map(field => [field.label, field]));

    // Add or replace fields from fieldsWithSubFields
    fieldsWithSubFields.forEach(subField => {
      fieldMap.set(subField.label!, subField);
    });

    // Convert map back to array
    return Array.from(fieldMap.values());
  }

  const equityField = userHasPermissionEnabled(user, PERMISSIONS.SHOW_EQUITY_ASSET_ALLOCATION)
    ? [
        {
          key: 'allocations_asset["Equity"]',
          label: formatMessage('Comparison.Table.AssetDetails.Equity'),
          cell: PercentageCell,
        },
      ]
    : [];

  const canadaField =
    appRegion === AppRegion.CAN
      ? [
          {
            key: 'allocations_asset["Canadian Equity"]',
            label: formatMessage('Comparison.Table.AssetDetails.CanadianEquity'),
            cell: PercentageCell,
          },
        ]
      : [];

  const allocationFields = [
    ...equityField,
    ...canadaField,
    {
      key: 'allocations_asset["United States Equity"]',
      label: formatMessage('Comparison.Table.AssetDetails.USEquity'),
      cell: PercentageCell,
    },
    {
      key: 'allocations_asset["Foreign Equity"]',
      label: formatMessage('Comparison.Table.AssetDetails.InternationalEquity'),
      cell: PercentageCell,
    },
    ...defaultFields,
  ];

  return allocationFields;
};

const getIndustryHoldingFields = (count: number = 5) => {
  const fields: ComparisonField[] = [];

  for (let i = 0; i < count; i++) {
    fields.push({
      key: `allocations_sector[${i}]`,
      label: formatMessage(`Common.Ordinal.${ordinal(i + 1)}`),
      cell: HoldingCell,
      accessor: item => {
        if (!item.allocations || !item.allocations.sector || !item.allocations.sector[i]) {
          return undefined;
        }

        return {
          weight: item.allocations.sector[i].weight,
          name: formatLocalizedString(item.allocations.sector[i].name),
        };
      },
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_BOND_INDUSTRY_HOLDINGS, DISCLAIMER_KEYS.FUND_MISSING_INDUSTRY_HOLDINGS],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_INDUSTRY_HOLDINGS],
      },
    });
  }

  return fields;
};

const getAlternativeInvestmentsFields = (): ComparisonField[] => [
  {
    key: 'manufacturer',
    label: formatMessage('Comparison.Table.AlternativeInvestment.Rows.FirmName'),
    cell: TextCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.GROUP]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
    },
  },
  {
    key: 'vintage',
    label: formatMessage('Comparison.Table.AlternativeInvestment.Rows.Vintage'),
    cell: TextCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.GROUP]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
    },
  },
  {
    key: 'itemStatus',
    label: formatMessage('Comparison.Table.AlternativeInvestment.Rows.Status'),
    accessor: item => item.status,
    cell: TextCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.GROUP]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
    },
  },
  {
    key: 'commitment.size',
    label: formatMessage('Comparison.Table.AlternativeInvestment.Rows.Commitment'),
    cell: CurrencyCell,
    accessor: item => ({
      value: item.commitment?.size,
    }),
  },
  {
    key: 'commitment.gpCommitmentValue',
    label: formatMessage('Comparison.Table.AlternativeInvestment.Rows.GPCommitmentsToFund'),
    cell: CurrencyCell,
    accessor: item => ({
      value: item.commitment?.gpCommitmentValue,
    }),
    disclaimers: {
      [COMPARISON_ITEM_TYPES.GROUP]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
    },
  },
  {
    key: 'called',
    label: formatMessage('Comparison.Table.AlternativeInvestment.Rows.Called'),
    cell: CurrencyCell,
    accessor: item => ({
      value: item.called,
    }),
  },
  {
    key: 'distributed',
    label: formatMessage('Comparison.Table.AlternativeInvestment.Rows.Distributed'),
    cell: CurrencyCell,
    accessor: item => ({
      value: item.distributed,
    }),
  },
  {
    key: 'remaining',
    label: formatMessage('Comparison.Table.AlternativeInvestment.Rows.Remaining'),
    cell: CurrencyCell,
    accessor: item => ({
      value: item.remaining,
    }),
  },
];

const getFeeInformationFields = (): ComparisonField[] => [
  {
    key: 'managementFee.investPeriod',
    label: formatMessage('Comparison.Table.FeeInformation.Rows.DuringInvestmentPeriod'),
    cell: PercentageCell,
  },
  {
    key: 'managementFee.postInvestPeriod',
    label: formatMessage('Comparison.Table.FeeInformation.Rows.PostInvestmentPeriod'),
    cell: PercentageCell,
  },
  {
    key: 'chargeFrequency',
    label: formatMessage('Comparison.Table.FeeInformation.Rows.ChargeFrequency'),
    cell: TextCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.GROUP]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
    },
  },
  {
    key: 'hurdleRate',
    label: formatMessage('Comparison.Table.FeeInformation.Rows.HurdleRate'),
    cell: PercentageCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.GROUP]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
    },
  },
  {
    key: 'carriedInterest.rate',
    label: formatMessage('Comparison.Table.FeeInformation.Rows.CarriedInterest'),
    cell: PercentageCell,
  },
  {
    key: 'gpCatchUpRate',
    label: formatMessage('Comparison.Table.FeeInformation.Rows.GPCatchUpRate'),
    cell: PercentageCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.GROUP]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
    },
  },
  {
    key: 'fundFormationCost',
    label: formatMessage('Comparison.Table.FeeInformation.Rows.FundFormationCost'),
    cell: CurrencyCell,
    accessor: item => ({
      value: item.fundFormationCost,
    }),
  },
];

const getAlternativePerformanceFields = (): ComparisonField[] => [
  {
    key: 'netIrr',
    label: formatMessage('Comparison.Table.AlternativePerformance.Rows.NetIRR'),
    cell: PercentageCell,
  },
  {
    key: 'medianBenchmarkNetIrr',
    label: formatMessage('Comparison.Table.AlternativePerformance.Rows.MedianBenchmarkIRR'),
    cell: PercentageCell,
  },
  {
    key: 'quartile',
    label: formatMessage('Comparison.Table.AlternativePerformance.Rows.Quartile'),
    cell: TextCell,
    disclaimers: {
      [COMPARISON_ITEM_TYPES.GROUP]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
      [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.NOT_AGGREGATED],
    },
  },
];

const getFixedIncomeMetricsDisclaimer = (item: ComparisonItem) => {
  if (
    item.disclaimers &&
    (item.disclaimers[DISCLAIMER_KEYS.FUND_FIXED_INCOME_METRICS_NOT_APPLICABLE] ||
      item.disclaimers[DISCLAIMER_KEYS.PORTFOLIO_FIXED_INCOME_METRICS_NOT_APPLICABLE])
  ) {
    return null;
  }

  // Check if we should hide date disclaimers for portfolios/groups
  if (shouldHideDateDisclaimers(item)) {
    return null;
  }

  const metricsDate = getAsAtDate(item, 'dates.characteristics');
  const allocationsDate = getAsAtDate(item, 'dates.allocations', 'dates.portfolioAllocations');
  const modifiedDurationDate = getAsAtDate(item, 'dates.investmentInfo', 'dates.portfolioInvestmentInfo');
  const ytmDurationDate = getAsAtDate(item, 'dates.yieldToMaturity', 'dates.portfolioYieldToMaturity');

  if (metricsDate && metricsDate === allocationsDate && metricsDate === modifiedDurationDate) {
    return formatMessage('Comparison.Table.FixedIncomeMetrics.SectionAsAt', {
      date: allocationsDate,
    });
  }

  const allocationsDisclaimer = !allocationsDate
    ? null
    : formatMessage('Comparison.Table.FixedIncomeMetrics.AllocationAsAt', {
        date: allocationsDate,
      });

  const modifiedDurationDisclaimer = !modifiedDurationDate
    ? null
    : formatMessage('Comparison.Table.FixedIncomeMetrics.ModifiedDurationAsAt', {
        date: modifiedDurationDate,
      });

  const otherMetricsDisclaimer = !metricsDate
    ? null
    : formatMessage('Comparison.Table.FixedIncomeMetrics.MetricsAsAt', {
        date: metricsDate,
      });

  const ytmDurationDisclaimer = !ytmDurationDate
    ? null
    : formatMessage('Comparison.Table.FixedIncomeMetrics.yieldToMaturityAsAt', {
        date: ytmDurationDate,
      });

  return [allocationsDisclaimer, modifiedDurationDisclaimer, ytmDurationDisclaimer, otherMetricsDisclaimer].filter(v => !!v).join(' ');
};

const getEquityMetricsDisclaimer = (item: ComparisonItem) => {
  if (
    item.disclaimers &&
    (item.disclaimers[DISCLAIMER_KEYS.FUND_EQUITY_METRICS_NOT_APPLICABLE] || item.disclaimers[DISCLAIMER_KEYS.PORTFOLIO_EQUITY_METRICS_NOT_APPLICABLE])
  ) {
    return null;
  }

  // Check if we should hide date disclaimers for portfolios/groups
  if (shouldHideDateDisclaimers(item)) {
    return null;
  }

  const allocationsDate = getAsAtDate(item, 'dates.allocations', 'dates.portfolioAllocations');
  const distributionYieldDate = getAsAtDate(item, 'dates.distributionYield');
  const investmentInfoDate = getAsAtDate(item, 'dates.investmentInfo', 'dates.portfolioInvestmentInfo');
  const characteristicsDate = item.type === COMPARISON_ITEM_TYPES.STOCK ? investmentInfoDate : getAsAtDate(item, 'dates.characteristics');

  if (
    investmentInfoDate &&
    (!characteristicsDate || investmentInfoDate === characteristicsDate) &&
    (!allocationsDate || investmentInfoDate === allocationsDate)
  ) {
    return formatMessage('Comparison.Table.EquityMetrics.SectionAsAt', {
      date: investmentInfoDate,
    });
  }

  const allocationsDisclaimer = !allocationsDate
    ? null
    : formatMessage('Comparison.Table.EquityMetrics.AllocationAsAt', {
        date: allocationsDate,
      });

  const distributionYieldDisclaimer = !distributionYieldDate
    ? null
    : formatMessage('Comparison.Table.EquityMetrics.DistributionYieldAsAt', {
        date: distributionYieldDate,
      });

  const returnDisclaimer = !characteristicsDate
    ? null
    : formatMessage('Comparison.Table.EquityMetrics.ReturnAsAt', {
        date: characteristicsDate,
      });

  const metricsDisclaimer = !investmentInfoDate
    ? null
    : formatMessage('Comparison.Table.EquityMetrics.OtherMetricsAsAt', {
        date: investmentInfoDate,
      });

  return [allocationsDisclaimer, distributionYieldDisclaimer, returnDisclaimer, metricsDisclaimer].filter(v => !!v).join(' ');
};

const getEquityStyleDisclaimer = (item: ComparisonItem, user: User) => {
  const equityBreakdownDisclaimers = [];
  if (
    item.disclaimers &&
    (item.disclaimers[DISCLAIMER_KEYS.FUND_EQUITY_STYLE_NOT_APPLICABLE] || item.disclaimers[DISCLAIMER_KEYS.PORTFOLIO_EQUITY_STYLE_NOT_APPLICABLE])
  ) {
    return null;
  }

  const investmentInfoDisclaimer = getDateDisclaimer(item, 'dates.investmentInfo', 'dates.portfolioInvestmentInfo', 'Comparison.Table.EquityStyle.EquityAsAt');
  if (investmentInfoDisclaimer) {
    equityBreakdownDisclaimers.push(investmentInfoDisclaimer);
  }

  const preferredProvider = getPreferredDataProvider(user, 'fundPortfolio.investmentInfo.capitalization');
  if (isPreferredProviderRefinitiv(preferredProvider)) {
    // Use allocations date for market cap disclaimer
    const marketCapDisclaimer = getDateDisclaimer(item, 'dates.allocations', 'dates.portfolioAllocations', 'Comparison.Table.EquityStyle.MarketCapAsAt');
    if (marketCapDisclaimer) {
      equityBreakdownDisclaimers.push(marketCapDisclaimer);
    }
  }

  return equityBreakdownDisclaimers.join(' ');
};

const getFixedIncomeStyleDisclaimer = (item: ComparisonItem) => {
  if (
    item.disclaimers &&
    (item.disclaimers[DISCLAIMER_KEYS.FUND_FIXED_INCOME_STYLE_NOT_APPLICABLE] || item.disclaimers[DISCLAIMER_KEYS.PORTFOLIO_FIXED_INCOME_STYLE_NOT_APPLICABLE])
  ) {
    return null;
  }

  return getDateDisclaimer(item, 'dates.investmentInfo', 'dates.portfolioInvestmentInfo', 'Comparison.Table.FixedIncomeStyle.AsAt');
};

const getCreditQualityDisclaimer = (item: ComparisonItem) => {
  if (
    item.disclaimers &&
    (item.disclaimers[DISCLAIMER_KEYS.FUND_CREDIT_QUALITY_NOT_APPLICABLE] || item.disclaimers[DISCLAIMER_KEYS.PORTFOLIO_CREDIT_QUALITY_NOT_APPLICABLE])
  ) {
    return null;
  }

  return getDateDisclaimer(item, 'dates.allocations', 'dates.portfolioAllocations', 'Comparison.Table.CreditQuality.AsAt');
};

const getMaturityAllocationsDisclaimer = (item: ComparisonItem) => {
  if (
    item.disclaimers &&
    (item.disclaimers[DISCLAIMER_KEYS.FUND_MATURITY_NOT_APPLICABLE] || item.disclaimers[DISCLAIMER_KEYS.PORTFOLIO_MATURITY_NOT_APPLICABLE])
  ) {
    return null;
  }

  return getDateDisclaimer(item, 'dates.allocations', 'dates.portfolioAllocations', 'Comparison.Table.MaturityAllocations.AsAt');
};

const getFixedIncomeRegionDisclaimer = (item: ComparisonItem) => {
  if (
    item.disclaimers &&
    (item.disclaimers[DISCLAIMER_KEYS.FUND_FIXED_INCOME_REGION_NOT_APPLICABLE] ||
      item.disclaimers[DISCLAIMER_KEYS.PORTFOLIO_FIXED_INCOME_REGION_NOT_APPLICABLE])
  ) {
    return null;
  }

  return getDateDisclaimer(item, 'dates.allocations', 'dates.portfolioAllocations', 'Comparison.Table.FixedIncomeRegions.AsAt');
};

const getFixedIncomeSectorDisclaimer = (item: ComparisonItem) => {
  if (
    item.disclaimers &&
    (item.disclaimers[DISCLAIMER_KEYS.FUND_FIXED_INCOME_SECTORS_NOT_APPLICABLE] ||
      item.disclaimers[DISCLAIMER_KEYS.PORTFOLIO_FIXED_INCOME_SECTORS_NOT_APPLICABLE])
  ) {
    return null;
  }

  return getDateDisclaimer(item, 'dates.fixedIncomeSector', 'dates.portfolioFixedIncomeSector', 'Comparison.Table.FixedIncomeSectors.AsAt');
};

export const getAltsMetricsDisclaimer = (item: ComparisonItem) => {
  const showSingleItemDisclaimer = item.type === COMPARISON_ITEM_TYPES.ALT && item.hasPerformanceFromBenchmark;
  const showPortfolioOrGroupDisclaimer =
    (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO || item.type === COMPARISON_ITEM_TYPES.GROUP) &&
    item.holdings.some(holding => holding.type === COMPARISON_ITEM_TYPES.ALT && holding.hasPerformanceFromBenchmark);

  if (showSingleItemDisclaimer) {
    return formatMessage('Comparison.Table.AlternativePerformance.AltDisclaimer');
  }

  if (showPortfolioOrGroupDisclaimer) {
    const altsWithBenchmark = item.holdings.filter(holding => holding.type === COMPARISON_ITEM_TYPES.ALT && holding.hasPerformanceFromBenchmark);
    return (
      <>
        <span>{formatMessage('Comparison.Table.AlternativePerformance.PortfolioGroupDisclaimer')}</span>
        <span>
          <InfoTooltip
            message={formatMessage('Comparison.Table.AlternativePerformance.PortfolioHoldingsDisclaimer', {
              holdings: Array.from(new Set(altsWithBenchmark.map(holding => formatLocalizedString(holding.name)))).join(', '),
            })}
          />
        </span>
      </>
    );
  }
  return null;
};

/**
 * Dynamically generates the comparison categories to be rendered inside the comparison table.
 *
 * @param user Currently authenticated user -- mostly used to evaluate permissions.
 * @returns Array of comparison categories.
 *
 *
 * ---------
 * MORE INFO
 * ---------
 *
 * Each comparison category is defined as follow:
 *
 *    key:        `string`  Unique key for the comparison category;
 *    label:      `string`  Translation key of the category's label - should be present in all translation files;
 *    info:       `string`  Disclaimer text to be displayed for the category;
 *    fields:     `array`   The fields included in this category (see field definition below).
 *
 *  Each comparison field is defined as follow:
 *
 *    key:              `string`      Path of the value inside the comparison item (see ComparisonItem type)
 *    label:            `string`      Translation key of the field's label;
 *    accessor:         `function`    Value accessor for values that are dynamic (MER fields);
 *    cell:             `component`   React component that will be used to render this specific field (defaults to TextCell);
 *    itemTypes:        `array`       Types for which this field applies (fund, stock or portfolio);
 *    disclaimers:      `object`      Object containing the disclaimer keys applicable for each item type (see definition below).
 *    hidden:           `boolean`     Boolean that determines if the field should be hidden or not (applies on top of the item types restriction);
 *    checkboxOptions:  `object`      Labels to be used next to the checkbox for both `true` and `false` values. [Only for CheckboxCell fields].
 *    valueDisclaimer:  `function`    Function that generates a disclaimer to be displayed next to the values for this field.
 *
 *  The disclaimers on the comparison fields are defined by comparison item type. The disclaimers defined should be the keys of the
 *  disclaimers to look for, ***in order of importance***. For example, the sharpe ratio may be missing on a fund because the fund has no data at all,
 *  or simply because it has no benchmark data. The priority should be given to "no data" as it is a much bigger issue than not having only the benchmarks data.
 *
 *  disclaimers: {
 *    fund: [...],
 *    stock: [...],
 *    portfolio: [...],
 *    benchmark: [...],
 * }
 *
 * The disclaimers on the comparison item are generated on a per-item basis -- see utils/comparison.ts inside the respective `{type}ToComparisonItem` functions.
 *
 * Note: Keys use '_' to separate values instead of '.' so what we can save the keys since we cant use '.' in mongoose.
 * When accessing a field, remember to replace the '_' with '.'
 */
export const getComparisonCategories = (user: User, comparison: Comparison, locale?: Locale, tabSelections?: Record<string, number>): ComparisonCategory[] => {
  const userHasPermission = (permission: string) => !!comparison.providedBy || userHasPermissionEnabled(user, permission);

  // You don't have permission or you don't have the FI metrics toggled on
  const hideFixedIncomeSubCategories =
    !userHasPermission(PERMISSIONS.FIXED_INCOME_METRICS) || !comparison.toggledCategories.includes(COMPARISON_CATEGORIES.FIXED_INCOME_METRICS);

  const isRegionUS = validateAppRegion(user.appConfig.appRegion, AppRegion.US);

  const hideEquitySubCategories =
    !userHasPermission(PERMISSIONS.EQUITY_METRICS) || !comparison.toggledCategories.includes(COMPARISON_CATEGORIES.EQUITY_METRICS);

  const hideAltSubCategories =
    !userHasPermission(PERMISSIONS.PREQIN_ALT) || !comparison.toggledCategories.includes(COMPARISON_CATEGORIES.ALTERNATIVE_INVESTMENT);

  const hideESGSubCategories = !userHasPermission(PERMISSIONS.ESG) || !comparison.toggledCategories.includes(COMPARISON_CATEGORIES.ESG_OVERVIEW);

  const hasEsgComparisonItems = comparison.items.some(item => item.esg);
  const isReadOnly = comparisonIsReadOnly(comparison);
  const disableESG = isReadOnly && !hasEsgComparisonItems;
  const hasAltHoldings = comparison.items.some(item => item.holdings.some(holding => holding.type === COMPARISON_ITEM_TYPES.ALT));
  const hasComparisonsTopHoldingsDynamicCount = userHasPermission(PERMISSIONS.COMPARISONS_TOP_HOLDINGS_DYNAMIC_COUNT);

  const hiddenCategories = user.appConfig?.comparison?.hiddenCategories || [];

  return [
    {
      key: COMPARISON_CATEGORIES.KEY_METRICS,
      label: formatMessage('Comparison.Table.KeyMetrics.Title'),
      getDisclaimer: (item: ComparisonItem) => {
        if (item.mer != null && item.trustedSource === FUND_SOURCE.FUNDATA) {
          return formatMessage('Disclaimers.FundataMER');
        }
        return null;
      },
      fields: [
        {
          key: 'performance_percentGrowth_1Y',
          label: formatMessage('Comparison.Table.Performance.1Y'),
          cell: GrowthCell,
          itemTypes: [
            COMPARISON_ITEM_TYPES.FUND,
            COMPARISON_ITEM_TYPES.PORTFOLIO,
            COMPARISON_ITEM_TYPES.STOCK,
            COMPARISON_ITEM_TYPES.GROUP,
            COMPARISON_ITEM_TYPES.ALT,
          ],
          disclaimers: TIME_BASED_DISCLAIMERS,
          getValueDisclaimer: getShortHistoryValueDisclaimer('performance_percentGrowth_1Y', comparison),
        },
        {
          key: 'performance_percentGrowth_3Y',
          label: formatMessage('Comparison.Table.Performance.3Y'),
          cell: GrowthCell,
          itemTypes: [
            COMPARISON_ITEM_TYPES.FUND,
            COMPARISON_ITEM_TYPES.PORTFOLIO,
            COMPARISON_ITEM_TYPES.STOCK,
            COMPARISON_ITEM_TYPES.GROUP,
            COMPARISON_ITEM_TYPES.ALT,
          ],
          disclaimers: TIME_BASED_DISCLAIMERS,
          getValueDisclaimer: getShortHistoryValueDisclaimer('performance_percentGrowth_3Y', comparison),
        },
        {
          key: 'performance_beta',
          label: formatMessage('RiskMetrics.Beta'),
          cell: StatCell,
          itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.GROUP],
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_BETA,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
              DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
              DISCLAIMER_KEYS.PORTFOLIO_NO_BENCHMARK_BETA,
              DISCLAIMER_KEYS.PORTFOLIO_RISK_METRICS_NOT_ENOUGH_DATA,
            ],
          },
          multiValue: true,
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
        },
        {
          key: 'mer',
          label: formatMessage('Comparison.Table.KeyMetrics.MER'),
          info: formatMessage('Glossary.MER.Definition.1'),
          cell: PercentageCell,
          itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
        },
        {
          key: 'discretionaryMer',
          label: formatMessage('Comparison.Table.KeyMetrics.DiscretionaryMER'),
          accessor: (item: ComparisonItem) => item.discretionaryMer || 0,
          cell: PercentageCell,
          itemTypes: [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          hidden: !userHasPermissionEnabled(user, PERMISSIONS.DISCRETIONARY_MER) || !comparison.items.some(item => !!item.discretionaryMer),
        },
        {
          key: 'tax',
          label: formatMessage('Comparison.Table.KeyMetrics.Tax'),
          info: formatMessage('Comparison.TaxDescription'),
          accessor: (item: ComparisonItem) => item.tax || 0,
          cell: PercentageCell,
          itemTypes: [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          hidden: !userHasPermissionEnabled(user, PERMISSIONS.TAXES) || !comparison.items.some(item => item.tax != null),
        },
        {
          key: 'totalMer',
          label: formatMessage('Comparison.Table.KeyMetrics.TotalMER'),
          accessor: (item: ComparisonItem) => (item.mer || 0) + (item.discretionaryMer || 0) + (item.tax || 0),
          cell: PercentageCell,
          itemTypes: [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          hidden: !userHasPermissionEnabled(user, PERMISSIONS.DISCRETIONARY_MER) || !comparison.items.some(item => !!item.discretionaryMer),
        },
        {
          key: 'classification',
          label: formatMessage('Comparison.Table.KeyMetrics.Classification'),
          itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.ALT],
        },
        {
          key: 'objective',
          label: formatMessage('Comparison.Table.KeyMetrics.Objective'),
          cell: ObjectiveCell,
          itemTypes: [COMPARISON_ITEM_TYPES.FUND],
        },
        {
          key: 'dates_inception',
          label: formatMessage('Comparison.Table.KeyMetrics.InceptionDate'),
          cell: DateCell,
          itemTypes: [COMPARISON_ITEM_TYPES.FUND],
        },
        {
          key: 'tna',
          label: formatMessage('Comparison.Table.KeyMetrics.TNA'),
          cell: CurrencyCell,
          itemTypes: [COMPARISON_ITEM_TYPES.FUND],
        },
        {
          key: 'currency',
          label: formatMessage('Comparison.Table.KeyMetrics.BaseCurrency'),
          itemTypes: [
            COMPARISON_ITEM_TYPES.FUND,
            COMPARISON_ITEM_TYPES.PORTFOLIO,
            COMPARISON_ITEM_TYPES.STOCK,
            COMPARISON_ITEM_TYPES.GROUP,
            COMPARISON_ITEM_TYPES.ALT,
          ],
          accessor: (item: ComparisonItem) => (item.currency === CURRENCY.MIXED ? formatMessage('Currencies.Mixed') : item.currency),
        },
        {
          key: 'domicile',
          label: formatMessage('Comparison.Table.KeyMetrics.Domicile'),
          itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.ALT],
        },
      ],
    },
    {
      key: COMPARISON_CATEGORIES.PERFORMANCE,
      label: formatMessage('Comparison.Table.Performance.Title'),
      info: formatMessage('Comparison.Table.Performance.Disclaimer'),
      getDisclaimer: (item: ComparisonItem) => getDateDisclaimer(item, 'dates.growth', 'dates.portfolioGrowth'),
      fields: getPerformanceFields(comparison, user, locale),
    },
    {
      key: COMPARISON_CATEGORIES.CALENDAR_YEAR_RETURNS,
      label: formatMessage('Comparison.Table.CalendarYear.Title'),
      info: formatMessage('Comparison.Table.CalendarYear.Disclaimer'),
      fields: getCalendarYearFields(comparison),
      itemTypes: [
        COMPARISON_ITEM_TYPES.ALT,
        COMPARISON_ITEM_TYPES.FUND,
        COMPARISON_ITEM_TYPES.PORTFOLIO,
        COMPARISON_ITEM_TYPES.STOCK,
        COMPARISON_ITEM_TYPES.BENCHMARK,
        COMPARISON_ITEM_TYPES.GROUP,
      ],
      controlled: true,
      hidden: !comparison.providedBy && !userHasPermissionEnabled(user, PERMISSIONS.CALENDAR_YEAR_RETURNS),
    },
    {
      key: COMPARISON_CATEGORIES.ASSET_DETAILS,
      label: formatMessage('Comparison.Table.AssetDetails.Title'),
      info: formatMessage('Disclaimers.AssetDetails'),
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MISSING_ASSET_DETAILS],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_ASSET_DETAILS],
      },
      getDisclaimer: (item: ComparisonItem) => getDateDisclaimer(item, 'dates.allocations', 'dates.portfolioAllocations', 'Comparison.Table.AssetDetails.AsAt'),
      fields: getAssetAllocationFields(user, comparison),
    },
    {
      key: COMPARISON_CATEGORIES.INDUSTRY_HOLDINGS,
      label: formatMessage('Comparison.Table.IndustryHoldings.Title'),
      info: formatMessage('Disclaimers.IndustryHoldings'),
      getDisclaimer: (item: ComparisonItem) =>
        getDateDisclaimer(item, 'dates.allocations', 'dates.portfolioAllocations', 'Comparison.Table.IndustryHoldings.AsAt'),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.GROUP],
      fields: getIndustryHoldingFields(),
    },
    {
      key: COMPARISON_CATEGORIES.RISK_METRICS,
      controlled: true,
      label: formatMessage('Comparison.Table.RiskMetrics.Core'),
      info: formatMessage('Comparison.Table.RiskMetrics.Disclaimer'),
      getDisclaimer: (item: ComparisonItem) => getDateDisclaimer(item, 'dates.risk.endDate', undefined, 'Comparison.Table.RiskMetrics.AsAt'),
      fields: [
        {
          key: 'riskRating',
          label: formatMessage('RiskMetrics.RiskRating'),
          itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.STOCK],
          cell: RiskRatingCell,
          accessor: (item: ComparisonItem) => {
            const { riskRating } = item;
            if (riskRating) {
              // Risk Rating is currently only displayed for funds (see itemTypes), which means it can only be 100% of one rating.
              const ratingKey = Object.keys(riskRating).find(key => riskRating[key] === 100);
              if (ratingKey) {
                return formatMessage(`RiskRating.${ratingKey}`);
              }
            }

            return null;
          },
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              // Vague TODO - Temporary solution. This should be generic and apply automatically
              // to all fields targeted by a custom provider.
              DISCLAIMER_KEYS.CUSTOM_PROVIDER_RISK_RATING,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [
              // Vague TODO - Temporary solution. This should be generic and apply automatically
              // to all fields targeted by a custom provider.
              DISCLAIMER_KEYS.CUSTOM_PROVIDER_RISK_RATING,
            ],
          },
          hidden: isRegionUS,
        },
        {
          key: 'benchmarks',
          label: formatMessage('RiskMetrics.Benchmarks'),
          cell: BenchmarkCell,
          getActiveBenchmark: () => comparison.items.find(item => item._id === comparison.selectedBenchmarkId),
          disabled: isReadOnly,
        },
        {
          key: 'performance_beta',
          label: formatMessage('RiskMetrics.Beta'),
          cell: StatCell,
          itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_BETA,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
              DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
              DISCLAIMER_KEYS.PORTFOLIO_NO_BENCHMARK_BETA,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
          },
          multiValue: true,
        },
        {
          key: 'performance_standardDeviation',
          label: formatMessage('RiskMetrics.StandardDeviation'),
          cell: StatCell,
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.BENCHMARK]: [DISCLAIMER_KEYS.BENCHMARK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
          },
          multiValue: true,
        },
        {
          key: 'performance_sharpeRatio',
          label: formatMessage('RiskMetrics.SharpeRatio'),
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          cell: StatCell,
          itemTypes: [
            COMPARISON_ITEM_TYPES.STOCK,
            COMPARISON_ITEM_TYPES.FUND,
            COMPARISON_ITEM_TYPES.PORTFOLIO,
            COMPARISON_ITEM_TYPES.BENCHMARK,
            COMPARISON_ITEM_TYPES.GROUP,
          ],
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_SHARPE,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
              DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
              DISCLAIMER_KEYS.PORTFOLIO_NO_BENCHMARK_SHARPE,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
          },
          multiValue: true,
        },
        {
          key: 'performance_maximumDrawdown',
          label: formatMessage('RiskMetrics.MaximumDrawdown'),
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          cell: PercentageCell,
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.BENCHMARK]: [DISCLAIMER_KEYS.BENCHMARK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
          },
          multiValue: true,
        },
        {
          key: 'performance_upsideDownside',
          label: formatMessage('RiskMetrics.UpsideDownside'),
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          accessor: (item: ComparisonItem, period?: RiskPeriods) => {
            const performance = !!comparison.showBaseCurrencies && !!item.isFX ? item.performanceBase || item.performance : item.performance;
            return {
              upside: performance.upside && performance.upside[period as RiskPeriods],
              downside: performance.downside && performance.downside[period as RiskPeriods],
            };
          },
          cell: UpDownCell,
          itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_UPDOWN,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
              DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
              DISCLAIMER_KEYS.PORTFOLIO_NO_BENCHMARK_UPDOWN,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
          },
          multiValue: true,
        },
        {
          key: 'performance_rSquared',
          label: formatMessage('RiskMetrics.RSquared'),
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          cell: StatCell,
          itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_RSQUARED,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
              DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
              DISCLAIMER_KEYS.PORTFOLIO_NO_BENCHMARK_RSQUARED,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
          },
          multiValue: true,
        },
        {
          key: 'performance_correlationCoefficient',
          label: formatMessage('RiskMetrics.CorrelationCoefficient'),
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          cell: StatCell,
          itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_CORRELATION_COEFFICIENT,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
              DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
              DISCLAIMER_KEYS.PORTFOLIO_NO_BENCHMARK_CORRELATION_COEFFICIENT,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
          },
          multiValue: true,
        },
        {
          key: 'performance_correlation',
          label: formatMessage('RiskMetrics.Correlation'),
          cell: CorrelationCell,
          itemTypes: [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          labelComponent: ComparisonCorrelationLabel,
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          disclaimers: {
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
          },
          multiValue: true,
        },
      ],
    },
    {
      key: COMPARISON_CATEGORIES.ADVANCED_RISK_METRICS,
      controlled: true,
      label: formatMessage('Comparison.Table.RiskMetrics.Advanced'),
      info: formatMessage('Comparison.Table.RiskMetrics.Disclaimer'),
      hidden: !comparison.providedBy && !userHasPermissionEnabled(user, PERMISSIONS.ADVANCED_RISK_METRICS),
      getDisclaimer: (item: ComparisonItem) => getDateDisclaimer(item, 'dates.risk.endDate', undefined, 'Comparison.Table.RiskMetrics.AsAt'),
      itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
      fields: [
        {
          key: 'performance_alpha',
          label: formatMessage('RiskMetrics.Alpha'),
          cell: StatCell,
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_ALPHA,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
              DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
              DISCLAIMER_KEYS.PORTFOLIO_NO_BENCHMARK_ALPHA,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
          },
          multiValue: true,
        },
        {
          key: 'performance_sortino',
          label: formatMessage('RiskMetrics.SortinoRatio'),
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          cell: StatCell,
          itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_SORTINO,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS, DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
              DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
              DISCLAIMER_KEYS.PORTFOLIO_NO_BENCHMARK_SORTINO,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
          },
          multiValue: true,
        },
        {
          key: 'performance_trackingError',
          label: formatMessage('RiskMetrics.TrackingError'),
          cell: StatCell,
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_TRACKING_ERROR,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [
              DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS,
              DISCLAIMER_KEYS.STOCK_MISSING_BENCHMARK_TRACKING_ERROR,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
              DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
              DISCLAIMER_KEYS.PORTFOLIO_NO_BENCHMARK_TRACKING_ERROR,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
          },
          multiValue: true,
        },
        {
          key: 'performance_infoRatio',
          label: formatMessage('RiskMetrics.InformationRatio'),
          cell: StatCell,
          getValueDisclaimer: getRiskMetricsShortHistoryValueDisclaimer(comparison),
          itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
          disclaimers: {
            [COMPARISON_ITEM_TYPES.FUND]: [
              DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
              DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_INFO_RATIO,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.STOCK]: [
              DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS,
              DISCLAIMER_KEYS.STOCK_MISSING_BENCHMARK_INFO_RATIO,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
            [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
              DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
              DISCLAIMER_KEYS.PORTFOLIO_NO_BENCHMARK_INFO_RATIO,
              DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
            ],
          },
          multiValue: true,
        },
      ],
    },
    {
      key: COMPARISON_CATEGORIES.LIPPER_LEADER_SCORES,
      label: formatMessage('Comparison.Table.LipperLeaderScores.Title'),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND],
      hideIfMixed: false,
      info: formatMessage('Disclaimers.LIPPER_LEADERS'),
      getDisclaimer: (item: ComparisonItem) => getDateDisclaimer(item, 'dates.lipperScore', undefined, 'Comparison.Table.LipperLeaderScores.AsAt'),
      fields: [
        {
          key: 'lipperLeaders_consistentReturn_overall_value',
          label: formatMessage('LipperLeaderScores.ConsistentReturn'),
          cell: LipperScoreCell,
        },
        {
          key: 'lipperLeaders_totalReturn_overall_value',
          label: formatMessage('LipperLeaderScores.TotalReturn'),
          cell: LipperScoreCell,
        },
        {
          key: 'lipperLeaders_preservation_overall_value',
          label: formatMessage('LipperLeaderScores.Preservation'),
          cell: LipperScoreCell,
        },
      ],
    },
    {
      key: COMPARISON_CATEGORIES.INVESTMENT_DETAILS,
      label: formatMessage('Comparison.Table.InvestmentDetails.Title'),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND],
      hideIfMixed: false,
      fields: [
        {
          key: 'minInvestments',
          label: formatMessage('Comparison.Table.InvestmentDetails.InitialInvestment'),
          cell: CurrencyCell,
        },
        {
          key: 'regularInvestments',
          label: formatMessage('Comparison.Table.InvestmentDetails.RegularInvestment'),
          cell: CurrencyCell,
        },
        {
          key: 'rrspEligible',
          label: formatMessage('Comparison.Table.InvestmentDetails.RrspEligibility'),
          itemTypes: [COMPARISON_ITEM_TYPES.FUND],
          cell: CheckboxCell,
          checkboxOptions: {
            true: 'Comparison.Table.InvestmentDetails.RrspEligibleOptions.true',
            false: 'Comparison.Table.InvestmentDetails.RrspEligibleOptions.false',
          },
          hidden: isRegionUS,
        },
      ],
    },
    {
      key: COMPARISON_CATEGORIES.TOP_HOLDINGS,
      info: formatMessage('Disclaimers.TopHoldings'),
      label: formatMessage(`Comparison.Table.TopHoldings.${hasComparisonsTopHoldingsDynamicCount ? 'MutipleTopHoldings' : 'Title'}`),
      getDisclaimer: (item: ComparisonItem) => getDateDisclaimer(item, 'dates.holdings', 'dates.portfolioHoldings', 'Comparison.Table.TopHoldings.AsAt'),
      fields: getTopHoldingsField(tabSelections?.[COMPARISON_CATEGORIES.TOP_HOLDINGS] || 10, hasComparisonsTopHoldingsDynamicCount),
      itemTypes: [COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
      ...(hasComparisonsTopHoldingsDynamicCount && {
        dynamicConfig: {
          type: 'multi-tab-select',
          options: [
            { label: formatMessage('Common.Numerical.10'), value: 10 },
            { label: formatMessage('Common.Numerical.25'), value: 25 },
            { label: formatMessage('Common.Numerical.50'), value: 50 },
          ],
          defaultValue: 10,
        },
      }),
    },
    {
      key: COMPARISON_CATEGORIES.COUNTRY_ALLOCATIONS,
      label: formatMessage('Comparison.Table.CountryAllocations.Title'),
      info: formatMessage('Disclaimers.CountryAllocations'),
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MISSING_COUNTRY_ALLOC],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_COUNTRY_ALLOC],
      },
      getDisclaimer: (item: ComparisonItem) =>
        getDateDisclaimer(item, 'dates.allocations', 'dates.portfolioAllocations', 'Comparison.Table.CountryAllocations.AsAt'),
      fields: getCountryAllocationsField(),
      itemTypes: [
        COMPARISON_ITEM_TYPES.STOCK,
        COMPARISON_ITEM_TYPES.FUND,
        COMPARISON_ITEM_TYPES.PORTFOLIO,
        COMPARISON_ITEM_TYPES.GROUP,
        COMPARISON_ITEM_TYPES.ALT,
      ],
    },
    {
      key: COMPARISON_CATEGORIES.EQUITY_METRICS,
      label: formatMessage('Comparison.Table.EquityMetrics.Title'),
      getDisclaimer: getEquityMetricsDisclaimer,
      fields: getEquityMetricsFields(user),
      toggleable: true,
      hidden: !comparison.providedBy && !userHasPermissionEnabled(user, PERMISSIONS.EQUITY_METRICS),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.GROUP],
    },
    {
      key: COMPARISON_CATEGORIES.EQUITY_STYLE_BREAKDOWN,
      label: formatMessage('Comparison.Table.EquityStyle.Title'),
      info: getEquityStyleBreakdownInfo(user),
      fields: getEquityStyleBreakdownFields(isRegionUS),
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MISSING_EQUITY_STYLE],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_EQUITY_STYLE],
      },
      getDisclaimer: (item: ComparisonItem) => getEquityStyleDisclaimer(item, user),
      hidden: hideEquitySubCategories,
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.GROUP],
    },
    {
      key: COMPARISON_CATEGORIES.FIXED_INCOME_METRICS,
      label: formatMessage('Comparison.Table.FixedIncomeMetrics.Title'),
      getDisclaimer: getFixedIncomeMetricsDisclaimer,
      fields: getFixedIncomeMetricsFields(user, comparison),
      toggleable: true,
      hidden: !comparison.providedBy && !userHasPermissionEnabled(user, PERMISSIONS.FIXED_INCOME_METRICS),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    },
    {
      key: COMPARISON_CATEGORIES.CREDIT_QUALITY,
      label: formatMessage('Comparison.Table.CreditQuality.Title'),
      info: formatMessage('Comparison.Table.CreditQuality.Info'),
      hidden: hideFixedIncomeSubCategories,
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MISSING_CREDIT_QUALITY],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_CREDIT_QUALITY],
      },
      getDisclaimer: getCreditQualityDisclaimer,
      fields: getCreditQualityFields(),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    },
    {
      key: COMPARISON_CATEGORIES.MATURITY_ALLOCATIONS,
      label: formatMessage('Comparison.Table.MaturityAllocations.Title'),
      hidden: hideFixedIncomeSubCategories,
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MISSING_MATURITY],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_MATURITY],
      },
      getDisclaimer: getMaturityAllocationsDisclaimer,
      fields: getMaturityAllocationFields(user),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    },
    {
      key: COMPARISON_CATEGORIES.FIXED_INCOME_STYLE,
      label: formatMessage('Comparison.Table.FixedIncomeStyle.Title'),
      info: formatMessage('Comparison.Table.SectionProvidedByProvider', {
        preferredDataProvider: getDataProviderName(user, 'fundPortfolio.allocations.creditQuality'),
      }),
      hidden: hideFixedIncomeSubCategories || isRegionUS,
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MISSING_FIXED_INCOME_STYLE],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_FIXED_INCOME_STYLE],
      },
      getDisclaimer: getFixedIncomeStyleDisclaimer,
      fields: getFixedIncomeStyleBreakdownFields(),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    },
    {
      key: COMPARISON_CATEGORIES.REGIONS,
      label: formatMessage('Comparison.Table.FixedIncomeRegions.Title'),
      hidden: hideFixedIncomeSubCategories,
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MISSING_FIXED_INCOME_REGIONS],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_FIXED_INCOME_REGIONS],
      },
      getDisclaimer: getFixedIncomeRegionDisclaimer,
      fields: getFixedIncomeRegionFields(),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    },
    {
      key: COMPARISON_CATEGORIES.FIXED_INCOME_SECTORS,
      label: formatMessage('Comparison.Table.FixedIncomeSectors.Title'),
      hidden: hideFixedIncomeSubCategories,
      disclaimers: {
        [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_MISSING_FIXED_INCOME_SECTORS],
        [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_MISSING_FIXED_INCOME_SECTORS],
      },
      getDisclaimer: getFixedIncomeSectorDisclaimer,
      fields: getFixedIncomeSectorFields(user),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    },
    {
      key: COMPARISON_CATEGORIES.ESG_OVERVIEW,
      label: formatMessage('Comparison.Table.ESGOverview.Title'),
      info: disableESG ? `${formatMessage('ESGComparisonTool.RerunComparison')}` : undefined,
      fields: getESGOverviewFields(comparison),
      toggleable: true,
      hidden: !userHasPermissionEnabled(user, PERMISSIONS.ESG),
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    },
    {
      key: COMPARISON_CATEGORIES.ESG_RATING_DISTRIBUTION,
      label: formatMessage('Comparison.Table.ESGOverview.ESGRatingDistribution'),
      fields: getESGRatingDistributionFields(comparison),
      hidden: hideESGSubCategories,
      itemTypes: [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
    },
    {
      key: COMPARISON_CATEGORIES.ALTERNATIVE_INVESTMENT,
      label: formatMessage('Comparison.Table.AlternativeInvestment.Title'),
      fields: getAlternativeInvestmentsFields(),
      toggleable: true,
      hidden: !hasAltHoldings || !userHasPermissionEnabled(user, PERMISSIONS.PREQIN_ALT),
      itemTypes: [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP, COMPARISON_ITEM_TYPES.ALT],
    },
    {
      key: COMPARISON_CATEGORIES.FEE_INFORMATION,
      label: formatMessage('Comparison.Table.FeeInformation.Title'),
      fields: getFeeInformationFields(),
      hidden: hideAltSubCategories,
      itemTypes: [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP, COMPARISON_ITEM_TYPES.ALT],
    },
    {
      key: COMPARISON_CATEGORIES.ALTERNATIVE_PERFORMANCE,
      label: formatMessage('Comparison.Table.AlternativePerformance.Title'),
      fields: getAlternativePerformanceFields(),
      hidden: hideAltSubCategories,
      getDisclaimer: (item: ComparisonItem) => getAltsMetricsDisclaimer(item),
      itemTypes: [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP, COMPARISON_ITEM_TYPES.ALT],
    },
  ].filter(category => !hiddenCategories.includes(category.key));
};

export const getComparisonCategoriesForAccessibleTable = (user: User, comparison: Comparison, locale?: Locale): ComparisonCategory[] => {
  const categories = getComparisonCategories(user, comparison, locale);

  // Update the Calendar Year Returns category to always show 10 years for accessibility
  return categories.map(category => {
    if (category.key === COMPARISON_CATEGORIES.CALENDAR_YEAR_RETURNS) {
      return {
        ...category,
        fields: getCalendarYearFields(comparison, true), // forAccessibleTable = true
      };
    }
    return category;
  });
};

export const shouldHideDateDisclaimers = (item: ComparisonItem): boolean =>
  item.type === COMPARISON_ITEM_TYPES.PORTFOLIO || item.type === COMPARISON_ITEM_TYPES.GROUP;

export const getDateDisclaimer = (
  item: ComparisonItem,
  singleDatePath: string,
  portfolioDatesPath?: string,
  messageKey: string = 'Comparison.Table.Performance.AsAt',
): string | null => {
  if (shouldHideDateDisclaimers(item)) {
    return null;
  }
  const formattedDate = getAsAtDate(item, singleDatePath, portfolioDatesPath);
  return formattedDate ? formatMessage(messageKey, { date: formattedDate }) : null;
};
